import React, { cloneElement, ReactElement } from 'react';
import { formatText } from '../../utils/format/formatText';
import { useTranslation } from '../../utils/useTranslation';
import { Skeleton } from '../feedback/Skeleton';
import { Tooltip } from '../feedback/Tooltip';
import { IconHelp } from '../icon/IconHelp';
import { Flex } from '../layout/Flex';
import { Typography } from '../typography/Typography';
import { PostStatUI } from './types';

interface StatItemProps {
    title: string;
    displayValue: string;
}

const StatItem = (props: StatItemProps) => {
    const { title, displayValue } = props;

    return (
        <Flex width="calc(100% / 3)" spacing={0.5} pb={2}>
            <Typography variant="caption" color="secondary">
                {title}
            </Typography>
            <Typography variant="title3" weight="bold">
                {displayValue}
            </Typography>
        </Flex>
    );
};

const StatItemSkeleton = () => (
    <Flex width="calc(100% / 3)" spacing={0.5} pb={2}>
        <Skeleton width="80px" variant="text" height={14} />
        <Skeleton width="80px" variant="text" height={24} />
    </Flex>
);

interface StatSectionProps {
    title: string;
    statsSlot: ReactElement;
    helpMessage: string;
}

const StatSection = (props: StatSectionProps) => {
    const { title, statsSlot, helpMessage } = props;

    return (
        <Flex
            background="var(--background-primary)"
            width="100%"
            pt={2}
            px={2}
            borderRadius={2}
            spacing={1.5}
        >
            <Flex direction="row" spacing={0.5} alignItems="center">
                <Typography variant="title3" weight="bold">
                    {title}
                </Typography>
                <Tooltip
                    trigger={<IconHelp color="var(--color-primary)" cursor="help" size={16} />}
                >
                    <Typography
                        variant="caption"
                        weight="bold"
                        color="var(--color-invert)"
                        display="inline"
                    >
                        {formatText(`${title}`)}
                    </Typography>
                    <Typography variant="caption" color="var(--color-invert)" display="inline">
                        {formatText(`${helpMessage}`)}
                    </Typography>
                </Tooltip>
            </Flex>
            <Flex width="100%" direction="row" flexWrap="wrap">
                {cloneElement(statsSlot)}
            </Flex>
        </Flex>
    );
};

interface StatSectionSkeletonProps {
    statsSlot: ReactElement;
}

const StatSectionSkeleton = (props: StatSectionSkeletonProps) => {
    const { statsSlot } = props;

    return (
        <Flex
            background="var(--background-primary)"
            width="100%"
            pt={2}
            px={2}
            borderRadius={2}
            spacing={1.5}
        >
            <Skeleton width="30%" variant="text" height={24} />
            <Flex width="100%" direction="row" flexWrap="wrap">
                {cloneElement(statsSlot)}
            </Flex>
        </Flex>
    );
};

interface PostStatisticProps {
    statistic: PostStatUI;
}

export const PostStatisticSkeleton = () => (
    <Flex width="100$" spacing={2} py={2}>
        <StatSectionSkeleton
            statsSlot={
                <>
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                </>
            }
        />
        <StatSectionSkeleton
            statsSlot={
                <>
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                </>
            }
        />
        <StatSectionSkeleton
            statsSlot={
                <>
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                </>
            }
        />
        <StatSectionSkeleton
            statsSlot={
                <>
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                </>
            }
        />
        <StatSectionSkeleton
            statsSlot={
                <>
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                </>
            }
        />
    </Flex>
);

export const PostStatistic = (props: PostStatisticProps) => {
    const t = useTranslation();
    const { statistic } = props;
    const { coverage, involved, shows, showsInGrid, subscribers, interactions } = statistic;

    return (
        <Flex width="100%" spacing={2} py={2}>
            {coverage ? (
                <StatSection
                    title={t('Охват')}
                    helpMessage={t(
                        ' - количество уникальных аккаунтов, владельцы которых хотя бы 1 раз видели публикацию за весь период времени существования публикации.\n' +
                            'Охват считается отдельно для пользователей- подписчиков и пользователей, которые не подписаны на профиль.',
                    )}
                    statsSlot={
                        <>
                            <StatItem title={t('Всего')} displayValue={coverage.total.display} />
                            <StatItem
                                title={t('Подписчики')}
                                displayValue={coverage.subscribers.display}
                            />
                            <StatItem
                                title={t('Не подписчики')}
                                displayValue={coverage.notsubscribers.display}
                            />
                        </>
                    }
                />
            ) : null}

            {involved ? (
                <StatSection
                    title={t('Вовлеченные  аккаунты')}
                    helpMessage={t(
                        ' - количество уникальных аккаунтов за все время существования публикации, владельцы которых совершили какое-либо полезное действие. Полезные действия - подписка, лайк, сохранение в избранное, поделиться контентом.\n' +
                            'Вовлеченные аккаунты считаются отдельно для пользователей- подписчиков и пользователей, которые не подписаны на профиль.',
                    )}
                    statsSlot={
                        <>
                            <StatItem title={t('Всего')} displayValue={involved.total.display} />
                            <StatItem
                                title={t('Подписчики')}
                                displayValue={involved.subscribers.display}
                            />
                            <StatItem
                                title={t('Не подписчики')}
                                displayValue={involved.notsubscribers.display}
                            />
                        </>
                    }
                />
            ) : null}

            {showsInGrid ? (
                <StatSection
                    title={t('Показы в ленте')}
                    helpMessage={t(' - все показы ваших постов в ленте.')}
                    statsSlot={
                        <>
                            <StatItem title={t('Всего')} displayValue={showsInGrid.total.display} />
                            <StatItem
                                title={t('Подписчики')}
                                displayValue={showsInGrid.subscribers.display}
                            />
                            <StatItem
                                title={t('Не подписчики')}
                                displayValue={showsInGrid.notsubscribers.display}
                            />
                        </>
                    }
                />
            ) : null}

            {shows ? (
                <StatSection
                    title={t('Показы с действием')}
                    helpMessage={t(
                        ' - показы с активным взаимодействием пользователя с постом, таких как открытие полноэкранного формата поста на ya.ru, открытие поста в вебе или приложении, наведение на пост, лайки, сохранения и тд.',
                    )}
                    statsSlot={
                        <>
                            <StatItem title={t('Всего')} displayValue={shows.total.display} />
                            <StatItem
                                title={t('Подписчики')}
                                displayValue={shows.subscribers.display}
                            />
                            <StatItem
                                title={t('Не подписчики')}
                                displayValue={shows.notsubscribers.display}
                            />
                        </>
                    }
                />
            ) : null}

            {subscribers ? (
                <StatSection
                    title={t('Подписчики')}
                    helpMessage={t(
                        ' - количество аккаунтов, подписавшихся на профиль с публикации за весь период существования публикации за вычетом отписавшихся. При этом можно посмотреть детально, сколько подписались, а сколько пользователей отписались.',
                    )}
                    statsSlot={
                        <>
                            <StatItem title={t('Всего')} displayValue={subscribers.total.display} />
                            <StatItem
                                title={t('Подписались')}
                                displayValue={subscribers.subscribed.display}
                            />
                            <StatItem
                                title={t('Отписались')}
                                displayValue={subscribers.unsubscribed.display}
                            />
                        </>
                    }
                />
            ) : null}

            {interactions ? (
                <StatSection
                    title={t('Взаимодействия')}
                    helpMessage={t(
                        ' - количество действий, которые совершали пользователи с публикаций за весь период времени.\n' +
                            'Клики по ссылкам - количество переходов по ссылкам из публикации.\n' +
                            'Лайки - количество лайков у публикации.\n' +
                            'Шаринги - количество шарингов публикацией.\n' +
                            'Сохранения - количество сохранений публикации в избранное.',
                    )}
                    statsSlot={
                        <>
                            <StatItem
                                title={t('Всего')}
                                displayValue={interactions.total.display}
                            />
                            <StatItem
                                title={t('Клики по ссылке')}
                                displayValue={interactions.clicks.display}
                            />
                            <StatItem
                                title={t('Лайки')}
                                displayValue={interactions.likes.display}
                            />
                            <StatItem
                                title={t('Шаринги')}
                                displayValue={interactions.shares.display}
                            />
                            <StatItem
                                title={t('Сохранения')}
                                displayValue={interactions.saves.display}
                            />
                        </>
                    }
                />
            ) : null}
        </Flex>
    );
};
